import { useCallback, useMemo, useState } from "react";

import PipelineTableView from "./PipelineTableView";

import type { ColumnData } from "@/features/player-kanban/types/types";
import type {
  PipelineTableSortDirection,
  PipelineTableSortKey,
} from "@/features/player-kanban/utils/pipelineTableRows";

import TagSelector from "@/features/player-kanban/components/organisms/TagSelector";
import { useTagMutations } from "@/features/player-kanban/hooks/useTagMutations";
import {
  buildPipelineTableRows,
  sortPipelineTableRows,
} from "@/features/player-kanban/utils/pipelineTableRows";

/**
 * Props for PipelineTable — the board's Table renderer (SE-5491).
 * @property columnsData - The board's columns, from `useKanbanBoard.tableColumnsData`
 * @property boardId - The board the rows belong to; required for tag editing
 * @property canEdit - Whether the viewer may edit the board's cards
 * @property onPlayerClick - Fired with the player id when a row is pressed
 */
export interface PipelineTableProps {
  columnsData: ColumnData[];
  boardId?: string;
  canEdit?: boolean;
  onPlayerClick?: (playerId: string) => void;
}

/**
 * Container for PipelineTable — flattens the board's columns into rows, owns
 * the table's local sort state, and wires the Tags column's edit affordances.
 * @remarks
 * SE-5491 AC1: the `columnsData` prop is the SAME derivation the Kanban
 * renderer consumes (`useKanbanBoard` → `generateColumnsData`), so toggling
 * between the two renderers cannot change which cards are in which stages.
 * There is no second query path here — the only query this container reaches
 * is the one `useTagMutations` shares (by identical variables, so one cache
 * entry) with the board's own card list.
 *
 * Sorting is view-local and non-mutating: it reorders a copy of the rows and
 * never touches the shared dataset.
 * @param props - Component props
 * @param props.columnsData - The board's columns, from `useKanbanBoard.tableColumnsData`
 * @param props.boardId - The board the rows belong to
 * @param props.canEdit - Whether the viewer may edit the board's cards
 * @param props.onPlayerClick - Fired with the player id when a row is pressed
 */
const PipelineTableContainer = ({
  columnsData,
  boardId,
  canEdit,
  onPlayerClick,
}: PipelineTableProps) => {
  const [sortKey, setSortKey] = useState<PipelineTableSortKey | null>(null);
  const [sortDirection, setSortDirection] =
    useState<PipelineTableSortDirection>("asc");
  // The player whose tag selector is open, or null when none is. The board's
  // existing selector is reused verbatim, so "Add" on a table row and the tag
  // flow on a card end in the same modal against the same mutations.
  const [tagSelectorPlayerId, setTagSelectorPlayerId] = useState<string | null>(
    null
  );

  const { removeTagsFromPlayer } = useTagMutations(boardId ?? "");

  const rows = useMemo(
    () =>
      sortPipelineTableRows(
        buildPipelineTableRows(columnsData),
        sortKey,
        sortDirection
      ),
    [columnsData, sortKey, sortDirection]
  );

  // Re-pressing the active column flips its direction; pressing a new column
  // starts it ascending. Exposed as a factory (not an inline arrow in the View)
  // so the View stays clear of react-perf/jsx-no-new-function-as-prop.
  const createSortHandler = useCallback(
    (key: PipelineTableSortKey) => () => {
      setSortDirection(previous =>
        key === sortKey && previous === "asc" ? "desc" : "asc"
      );
      setSortKey(key);
    },
    [sortKey]
  );

  const createRowPressHandler = useCallback(
    (playerId: string) => () => onPlayerClick?.(playerId),
    [onPlayerClick]
  );

  const createTagRemoveHandler = useCallback(
    (playerId: string, tagId: string) => () => {
      // Fire-and-forget, like the card's own removal: the mutation carries an
      // optimistic response, so the chip leaves on press and a failure surfaces
      // through the hook's shared error state rather than a per-chip await.
      void removeTagsFromPlayer(playerId, [tagId]);
    },
    [removeTagsFromPlayer]
  );

  const createAddTagHandler = useCallback(
    (playerId: string) => () => setTagSelectorPlayerId(playerId),
    []
  );

  const handleCloseTagSelector = useCallback(
    () => setTagSelectorPlayerId(null),
    []
  );

  // Tag editing needs both a board to mutate against and the right to do it.
  const canEditTags = Boolean(boardId) && Boolean(canEdit);

  return (
    <>
      <PipelineTableView
        rows={rows}
        sortKey={sortKey}
        sortDirection={sortDirection}
        createSortHandler={createSortHandler}
        createRowPressHandler={createRowPressHandler}
        createTagRemoveHandler={
          canEditTags ? createTagRemoveHandler : undefined
        }
        createAddTagHandler={canEditTags ? createAddTagHandler : undefined}
      />
      {tagSelectorPlayerId && boardId ? (
        <TagSelector
          isOpen
          onClose={handleCloseTagSelector}
          playerId={tagSelectorPlayerId}
          boardId={boardId}
        />
      ) : null}
    </>
  );
};

PipelineTableContainer.displayName = "PipelineTableContainer";

export default PipelineTableContainer;
